import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Divider,
  Chip,
  Stack,
  Link,
  useTheme,
} from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import BusinessIcon from "@mui/icons-material/Business";
import EditIcon from "@mui/icons-material/Edit";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";

import { tokens } from "../theme";
import PageContainer from "../layouts/PageContainer";
import AppButton from "../components/buttons/AppButton";
import DetailItem from "../components/common/DetailItem";
import UpdateSchoolModal from "../components/school/UpdateSchoolModal";
import AppCircularProgress from "../components/common/AppCircularProgress";
import PageError from "../components/common/PageError";

import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getSchool,
  resetSchoolServerStatus,
} from "../redux/actions/schoolActions";
import { usePermissions } from "../hooks/usePermissions";
import { formatDateForDisplay } from "../helpers/dateHelpers";

const SchoolPage: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const {
    school,
    serverStatus,
    loadingSchool,
    errorLoadingSchool,
    loadingSchoolUpdate,
    errorUpdatingSchool,
  } = useAppSelector((state) => state.school);

  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const canUpdate = hasPermission("school:update");

  useEffect(() => {
    dispatch(getSchool());
  }, [dispatch]);

  const handleOpenEdit = (): void => setIsUpdateModalOpen(true);
  const handleCloseEdit = useCallback((): void => {
    if (!loadingSchoolUpdate) setIsUpdateModalOpen(false);
  }, [loadingSchoolUpdate]);

  useEffect(() => {
    if (serverStatus === 200) {
      handleCloseEdit();
      dispatch(resetSchoolServerStatus());
    }
  }, [serverStatus, dispatch, handleCloseEdit]);

  const renderContent = (): React.ReactNode => {
    if (loadingSchool) {
      return <AppCircularProgress />;
    }

    if (errorLoadingSchool) {
      return (
        <PageError
          error={errorLoadingSchool}
          onRetry={() => dispatch(getSchool())}
        />
      );
    }

    if (!school) {
      return (
        <Box sx={{ textAlign: "center", mt: 5 }}>
          <Typography sx={{ color: "text.secondary" }}>
            No school data available.
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "7fr 5fr" },
          gap: 3,
        }}
      >
        {/* LEFT COLUMN: School Details & Address */}
        <Stack spacing={3}>
          {/* Main Identity Card */}
          <Card
            sx={{
              borderRadius: 2,
              border: `1px solid ${colors.grey[700]}`,
              bgcolor: "background.paper",
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Stack
                direction="row"
                spacing={2}
                sx={{ alignItems: "center", mb: 3 }}
              >
                {school.logoUrl ? (
                  <Avatar
                    src={school.logoUrl}
                    alt={school.name}
                    sx={{ width: 60, height: 60 }}
                  />
                ) : (
                  <Avatar
                    sx={{
                      background:
                        colors.gradients?.primary || theme.palette.primary.main,
                      width: 60,
                      height: 60,
                    }}
                  >
                    <SchoolIcon sx={{ fontSize: 30, color: "#fff" }} />
                  </Avatar>
                )}
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {school.name}
                  </Typography>
                  <Chip
                    label={`Reg No: ${school.registrationNumber}`}
                    color="secondary"
                    size="small"
                    sx={{ mt: 0.5, fontWeight: "bold" }}
                  />
                </Box>
              </Stack>

              <Divider sx={{ mb: 3 }} />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 3,
                }}
              >
                <DetailItem label="Email Address" value={school.contactEmail} />
                <DetailItem label="Phone Number" value={school.contactPhone} />
                <DetailItem
                  label="Website"
                  value={
                    school.website ? (
                      <Link
                        href={school.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        color="info"
                        sx={{ textDecoration: "underline" }}
                      >
                        {school.website}
                      </Link>
                    ) : (
                      "N/A"
                    )
                  }
                />
                <DetailItem
                  label="Registered On"
                  value={formatDateForDisplay(school.createdAt)}
                />
              </Box>

              <Box
                sx={{
                  mt: 4,
                  p: 2,
                  bgcolor: colors.grey[800],
                  borderRadius: 1,
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: "center", mb: 1 }}
                >
                  <FormatQuoteIcon sx={{ color: "action.active" }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    Motto: {school.motto || "N/A"}
                  </Typography>
                </Stack>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mb: 1 }}
                >
                  <b>Vision:</b> {school.vision || "N/A"}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  <b>Mission:</b> {school.mission || "N/A"}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Address Card */}
          <Card
            sx={{
              borderRadius: 2,
              border: `1px solid ${colors.grey[700]}`,
              bgcolor: "background.paper",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack
                direction="row"
                spacing={2}
                sx={{ alignItems: "center", mb: 2 }}
              >
                <LocationCityIcon sx={{ color: "action.active" }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Head Office Address
                </Typography>
              </Stack>
              <Divider sx={{ mb: 2 }} />

              {school.headOfficeAddress ? (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                  }}
                >
                  <DetailItem
                    label="Street 1"
                    value={school.headOfficeAddress.streetLine1}
                  />
                  <DetailItem
                    label="Street 2"
                    value={school.headOfficeAddress.streetLine2 || "-"}
                  />
                  <DetailItem
                    label="City"
                    value={school.headOfficeAddress.city}
                  />
                  <DetailItem
                    label="State / Province"
                    value={school.headOfficeAddress.stateOrProvince}
                  />
                  <DetailItem
                    label="Postal Code"
                    value={school.headOfficeAddress.postalCode}
                  />
                  <DetailItem
                    label="Country"
                    value={school.headOfficeAddress.country}
                  />
                </Box>
              ) : (
                <Typography sx={{ color: "text.secondary" }}>
                  No head office address configured.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Stack>

        {/* RIGHT COLUMN: Branches List */}
        <Box>
          <Card
            sx={{
              height: "100%",
              borderRadius: 2,
              border: `1px solid ${colors.grey[700]}`,
              bgcolor: "background.paper",
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Stack
                direction="row"
                spacing={2}
                sx={{ alignItems: "center", mb: 3 }}
              >
                <Avatar
                  sx={{
                    background:
                      colors.gradients?.success || theme.palette.success.main,
                    width: 50,
                    height: 50,
                  }}
                >
                  <BusinessIcon sx={{ fontSize: 26, color: "#fff" }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    Associated Campuses
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Branch Locations
                  </Typography>
                </Box>
              </Stack>
              <Divider sx={{ mb: 3 }} />

              {school.branches && school.branches.length > 0 ? (
                <Stack spacing={2}>
                  {school.branches.map((branch) => (
                    <Box
                      key={branch.id}
                      sx={{
                        p: 2,
                        border: `1px solid ${colors.grey[600]}`,
                        borderRadius: 1,
                      }}
                    >
                      <Stack
                        direction="row"
                        sx={{
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 1,
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: "bold" }}
                        >
                          {branch.name}
                        </Typography>
                        <Chip
                          label={branch.branchStatus}
                          size="small"
                          color={
                            branch.branchStatus === "ACTIVE"
                              ? "success"
                              : "default"
                          }
                        />
                      </Stack>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          color: "text.secondary",
                          mb: 1,
                        }}
                      >
                        Code: {branch.branchCode} | Principal:{" "}
                        {branch.principalName}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.primary" }}
                      >
                        {branch.address?.city}, {branch.address?.country}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        {branch.contactPhone} • {branch.contactEmail}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography
                  sx={{ color: "text.secondary", textAlign: "center", mt: 3 }}
                >
                  No branches associated with this school yet.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  };

  return (
    <PageContainer
      title="School Profile"
      subtitle="View and manage the master configuration for your institution."
      actions={
        canUpdate &&
        school && (
          <AppButton
            variant="contained"
            startIcon={<EditIcon />}
            onClick={handleOpenEdit}
            sx={{ background: colors.gradients?.info, color: "#fff" }}
          >
            Edit Details
          </AppButton>
        )
      }
    >
      {renderContent()}

      {isUpdateModalOpen && school && (
        <UpdateSchoolModal
          open={isUpdateModalOpen}
          onClose={handleCloseEdit}
          school={school}
          loading={loadingSchoolUpdate}
          error={errorUpdatingSchool}
        />
      )}
    </PageContainer>
  );
};

export default SchoolPage;
